// Session deep links live in the URL *path* (…/program-guide/<sessionId>), not in a query param,
// so that every session has one canonical, shareable address. The guide keeps that path in sync
// with the current selection and the share buttons publish it, so both build it from the rules
// here rather than each assembling URLs of their own.
//
// A presentation inside a session gets one more segment (…/program-guide/<sessionId>/<presentationId>).
// It is only ever meaningful after a session id, so the helpers below never write a presentation
// segment without one.
//
// The segment layout mirrors the slug parsing in pages/program-guide/[[...slug]].js: on the
// alternate route the first two segments name the page (["alt", <pageSlug>]) and the session id
// follows them; on the normal route the id is the first segment.
//
// Query mode. When the guide is embedded somewhere that owns its own path — a CMS page rendering the
// advancedAgenda "presentations-twoColumn" matrix block — the ids go in `?sessionId=…&presentationId=…`
// on whatever page it is mounted on instead, so nothing depends on a particular route pattern. The
// guide provides the mode through SessionUrlModeContext so the panels' links and share buttons follow.

import { createContext, useContext } from "react"
import { useRouter } from "next/router"

export const SESSION_URL_MODES = { PATH: "path", QUERY: "query" }

export const SessionUrlModeContext = createContext(SESSION_URL_MODES.PATH)

function toSegments(slugParam) {
    if (Array.isArray(slugParam)) return slugParam
    return slugParam ? [slugParam] : []
}

function baseLength(segments) {
    return segments[0] === "alt" ? 2 : 0
}

// The page-identifying segments, with any session/presentation ids stripped off.
export function baseSlugSegments(slugParam) {
    const segments = toSegments(slugParam)
    return segments.slice(0, baseLength(segments))
}

// The session id currently named by the path, or null.
export function sessionIdFromSlug(slugParam) {
    const segments = toSegments(slugParam)
    return segments[baseLength(segments)] ?? null
}

// The presentation id currently named by the path, or null.
export function presentationIdFromSlug(slugParam) {
    const segments = toSegments(slugParam)
    return segments[baseLength(segments) + 1] ?? null
}

// The full slug array for a given session (and optionally one of its presentations) — or just the
// page segments when `sessionId` is null, which is how the ids are cleared from the URL. A
// presentation id without a session id is ignored rather than written to a meaningless position.
export function sessionSlugSegments(slugParam, sessionId, presentationId = null) {
    const base = baseSlugSegments(slugParam)
    if (sessionId == null) return base
    const slug = [...base, String(sessionId)]
    if (presentationId != null) slug.push(String(presentationId))
    return slug
}

// Path for one session or presentation, derived from the route this guide is actually mounted on.
// Taking the base from `routePathname` (e.g. "/program-guide/[[...slug]]") rather than hardcoding
// it means share links follow the guide when the redesign moves from /program-guide/alt/<slug> to
// /program-guide.
export function sessionPath(routePathname, slugParam, sessionId, presentationId = null) {
    const base = routePathname.replace(/\/\[\[?\.\.\.[^\]]*\]?\].*$/, "")
    return [base, ...sessionSlugSegments(slugParam, sessionId, presentationId)].join("/")
}

// Absolute URL for share buttons. Deliberately carries no filter, day, or search params: a shared
// link has to resolve the same way for every recipient regardless of what the sharer had applied,
// and one canonical URL per session keeps Facebook from caching a separate preview per permutation.
// Returns "" during SSR, where there is no origin to build on.
export function sessionShareUrl(routePathname, slugParam, sessionId, presentationId = null) {
    if (typeof window === "undefined") return ""
    return window.location.origin + sessionPath(routePathname, slugParam, sessionId, presentationId)
}

// The program guide's own path for a session (or one of its presentations), for links from outside
// the guide — the chatbot's session cards — where there is no guide route to derive it from.
// `sessionId` is the top-level session the guide lists; a presentation may sit at any depth under it.
export function programGuidePath(sessionId, presentationId = null) {
    return ["/program-guide", ...sessionSlugSegments([], sessionId, presentationId)].join("/")
}

// Query-mode counterpart of sessionPath: the current page's path plus only the id params.
export function sessionQueryPath(asPath, sessionId, presentationId = null) {
    const base = asPath.split(/[?#]/)[0]
    const params = new URLSearchParams({ sessionId: String(sessionId) })
    if (presentationId != null) params.set("presentationId", String(presentationId))
    return `${base}?${params}`
}

// Link builders for the current route and URL mode, for components inside the guide.
export function useSessionLinks() {
    const router = useRouter()
    const mode = useContext(SessionUrlModeContext)

    function path(sessionId, presentationId = null) {
        return mode === SESSION_URL_MODES.QUERY
            ? sessionQueryPath(router.asPath, sessionId, presentationId)
            : sessionPath(router.pathname, router.query.slug, sessionId, presentationId)
    }

    function shareUrl(sessionId, presentationId = null) {
        if (typeof window === "undefined") return ""
        return window.location.origin + path(sessionId, presentationId)
    }

    return { path, shareUrl }
}

// Depth-first search for a presentation anywhere under a session. Emma nests presentations one or
// two levels down (some sessions wrap their presentations in a single same-titled child), so the
// whole subtree is walked rather than only direct children. Returns null when not found.
export function findPresentation(session, presentationId) {
    if (!session || presentationId == null) return null
    const id = parseInt(presentationId, 10)
    if (Number.isNaN(id)) return null

    const stack = [...(session.children || [])]
    while (stack.length) {
        const node = stack.shift()
        if (node.id === id) return node
        if (node.children?.length) stack.push(...node.children)
    }
    return null
}
